import EventUtils from '@rio-cloud/rio-uikit/EventUtils';
import { CenterMapOnPosition, Map as HereMap, MapScaleBarUnits, Route, SingleMapMarker } from 'components/map/Map';
import type { MapLayer, Position } from 'components/map/types';
import { getMapCredentials } from 'configuration';
import type { HubFE } from 'domain/hub';
import type { MissionFE } from 'domain/mission';
import { toRioPosition } from 'helper/position';
import type { DeviationWithVIN } from 'helper/types';
import useCenterVehicle from 'hooks/useCenterVehicle';
import { useDeepStable } from 'hooks/useDeepStable';
import { memo, type PropsWithChildren, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isDeepEqual } from 'remeda';
import { DEFAULT_LOCALE } from 'store/i18n/lang';
import {
    clearPVLTrigger,
    getFollowSelectedVehicle,
    getIsPVLTriggered,
    getMapLayers,
    getSelectedVehicle,
    getShowHubs,
    setFollowSelectedVehicle,
    setIsFullMissionRouteVisible,
    setMapLayers,
} from 'store/slices/appSlice';
import { getUseImperialUnits } from 'store/slices/userSettingsSlice';

import CustomMapSettings from './CustomMapSettings';
import HubMarker from './HubMarker';
import { useBoundingBox } from './hooks/useBoundingBox';
import { getRouteColor, resolveMissionForVehicle } from './utils';
import VehicleMarker from './VehicleMarker';

import type { VehicleV2 } from 'API';

// import { useFocusMissionRoute } from './hooks/useFocusMissionRoute'

const mapCredentials = getMapCredentials();

// SingleMapMarker drops unknown props, so the test ids sit on a wrapper (it becomes the HERE icon root).
const ROUTE_START_ICON = (
    <div data-testid='map-route-start-marker'>
        <SingleMapMarker iconNames={['start']} markerColor='bg-map-marker-route' />
    </div>
);
const ROUTE_END_ICON = (
    <div data-testid='map-route-finish-marker'>
        <SingleMapMarker iconNames={['finish']} markerColor='bg-map-marker-route' />
    </div>
);
const ROUTE_ARROW_STYLE = {};

interface MapProps {
    activeMission?: MissionFE | null;
    hubs?: HubFE[];
    vehicles: VehicleV2[];
    deviations?: DeviationWithVIN[];
    selectedVehicle?: VehicleV2;
    center?: Position;
    zoom?: number;
    selectedVehicleState?: any;
}

const Map = ({ hubs, vehicles, selectedVehicle, activeMission, children }: PropsWithChildren<MapProps>) => {
    const mapLayers = useSelector(getMapLayers);
    const showHubs = useSelector(getShowHubs);
    const isVehicleFocused = useSelector(getFollowSelectedVehicle);
    const selectedVehicleState = useSelector(getSelectedVehicle);
    const isPVLTriggered = useSelector(getIsPVLTriggered);
    const useImperialUnits = useSelector(getUseImperialUnits);
    const selectedVIN = selectedVehicleState?.selectedVIN;

    const dispatch = useDispatch();

    const { boundingBox, focusOnMissionRoute, updateBoundingBox, resetBoundingBox } = useBoundingBox(vehicles);
    const { center, reset: resetCenter } = useCenterVehicle(selectedVehicle);

    const missionForSelectedVehicle = useMemo(
        () => resolveMissionForVehicle(activeMission, selectedVehicle),
        [activeMission, selectedVehicle]
    );

    const showRoute = !!missionForSelectedVehicle?.route?.positions?.length;

    /**
     * Does the route we are currently holding actually belong to the vehicle that is selected?
     *
     * The selection and the mission arrive separately. When you switch from vehicle A to vehicle B,
     * B's mission has to be loaded, so for a render or two we still hold A's mission. Without this
     * check the reveal below would fire on A's route and use up its one-shot trigger, and B's route
     * would then never be shown.
     */
    const routeMatchesSelection = showRoute && missionForSelectedVehicle?.vin === selectedVIN;

    const focusOnSelectedVehicle = () => {
        resetBoundingBox();
        dispatch(setFollowSelectedVehicle(!isVehicleFocused));
        dispatch(setIsFullMissionRouteVisible(false));
    };

    const unfollowSelectedVehicle = useCallback(() => {
        dispatch(setFollowSelectedVehicle(false));
    }, [dispatch]);

    const showVehicleMissionRoute = () => {
        if (selectedVehicle && missionForSelectedVehicle?.route) {
            const missionWaypoints = missionForSelectedVehicle.route.positions;
            dispatch(setIsFullMissionRouteVisible(true));
            unfollowSelectedVehicle();
            focusOnMissionRoute(selectedVehicle, missionWaypoints);
        }
    };

    const resetToBoundingBox = () => {
        unfollowSelectedVehicle();
        dispatch(setIsFullMissionRouteVisible(false));
        resetCenter();
        updateBoundingBox();
    };

    const eventListenerMap = useMemo(
        () => ({
            [EventUtils.DRAG]: unfollowSelectedVehicle,
        }),
        [unfollowSelectedVehicle]
    );

    /**
     * The route geometry and style must keep a stable identity between renders. UIKit's Route
     * rebuilds its H.geo.LineString whenever `positions` changes identity and rebuilds its
     * H.map.SpatialStyle objects whenever `style` does, then pushes both into its Polylines.
     * Rebuilding a new array and a new style object on every telemetry tick is what makes the
     * vehicle path flash.
     */
    // Deep-stable: the mission is re-delivered with a brand-new positions array on every
    // subscription tick even when the route is unchanged, and a new array means a new
    // H.geo.LineString, which makes UIKit's Polyline rebuild its HERE map objects.
    // Taken from the resolved mission rather than the raw prop, so geometry and colour can never
    // come from a mission that failed the vehicle check above.
    const stableRoutePositions = useDeepStable(missionForSelectedVehicle?.route?.positions);

    const routePositions = useMemo(() => stableRoutePositions?.map(toRioPosition) ?? [], [stableRoutePositions]);

    const routeStyle = useMemo(
        () => ({
            width: 5,
            borderWidth: 2,
            isBorderIncludedInWidth: false,
            ...getRouteColor(missionForSelectedVehicle?.state?.value?.value),
        }),
        [missionForSelectedVehicle?.state?.value?.value]
    );

    /**
     * Shows the whole mission route once, just after the operator picked a vehicle from the list,
     * the search box or the sidebar.
     *
     * Why this needs an effect at all: at the moment of the click we only know the mission id. The
     * route itself (the GPS points we zoom the map to) arrives a bit later from the server. So the
     * click leaves a note behind — `pvlTriggered` — and we wait here for the route to turn up.
     *
     * Two things to keep in mind if you touch this:
     *
     * 1. We MUST clear the note once we have used it. It is a "this just happened" flag, not
     *    permanent state. Left set, this stops being a one-off and runs again and again.
     *
     * 2. Do NOT put `activeMission` in the dependency array. React compares dependencies by
     *    reference, and `activeMission` comes from a live subscription that builds a brand new
     *    object on every server update — so `{...} !== {...}` even when nothing changed. Depending
     *    on it made this run on every update, which un-followed the vehicle and zoomed back out to
     *    the whole route while the operator was watching a moving truck (ACC-2897). Depend on plain
     *    strings and booleans instead: those compare by value.
     */
    useEffect(() => {
        if (!isPVLTriggered || !selectedVIN || !routeMatchesSelection) {
            return;
        }
        showVehicleMissionRoute();
        dispatch(clearPVLTrigger()); // used the note, now throw it away
    }, [selectedVIN, routeMatchesSelection, isPVLTriggered]);

    /**
     * TODO: to work over zoom feature
     * - reset boundingBox (or add zoom variable)
     * - possibility of working with onZoomIn and onZoomOut events
     */
    return (
        <HereMap
            eventListenerMap={eventListenerMap}
            credentials={mapCredentials}
            boundingBox={boundingBox}
            language={DEFAULT_LOCALE}
            mapLayer={mapLayers}
            enableWebGL
            showScaleBar
            enableDevicePixelRatio
            onMapLayerChange={(activeMapLayers: MapLayer[]) => dispatch(setMapLayers(activeMapLayers))}
            // mapSettings={<MapSettings options={getMapSettingsOptions()} />}
            mapSettings={
                <CustomMapSettings
                    selectedVehicle={selectedVehicle}
                    hasActiveMission={!!missionForSelectedVehicle}
                    resetToBoundingBox={resetToBoundingBox}
                    unfollowSelectedVehicle={unfollowSelectedVehicle}
                    focusOnSelectedVehicle={focusOnSelectedVehicle}
                    showVehicleMissionRoute={showVehicleMissionRoute}
                />
            }
        >
            {() => (
                <>
                    {/* Inside the map, so it can re-centre through the map api and leave the zoom alone. */}
                    <CenterMapOnPosition center={center} animate={true} />
                    {/* Inside the map too: the HERE UI instance is only on the map context. */}
                    <MapScaleBarUnits imperial={useImperialUnits} />
                    {children}
                    {vehicles.map(vehicle => (
                        <VehicleMarker key={vehicle.vin} vehicle={vehicle} />
                    ))}
                    {showHubs && hubs?.map(hub => <HubMarker key={hub.id} hub={hub} />)}
                    {showRoute && (
                        <Route
                            positions={routePositions}
                            startIcon={ROUTE_START_ICON}
                            endIcon={ROUTE_END_ICON}
                            hasArrows={false}
                            arrowStyle={ROUTE_ARROW_STYLE}
                            style={routeStyle}
                        />
                    )}
                </>
            )}
        </HereMap>
    );
};

const arePropsEqual = (prevProps: MapProps, nextProps: MapProps) =>
    isDeepEqual(prevProps.center, nextProps.center) &&
    isDeepEqual(prevProps.zoom, nextProps.zoom) &&
    isDeepEqual(prevProps.hubs, nextProps.hubs) &&
    isDeepEqual(prevProps.selectedVehicleState, nextProps.selectedVehicleState) &&
    isDeepEqual(
        prevProps.vehicles.map(_ => _.position),
        nextProps.vehicles.map(_ => _.position)
    ) &&
    isDeepEqual(prevProps.deviations, nextProps.deviations) &&
    isDeepEqual(prevProps.activeMission, nextProps.activeMission) &&
    prevProps.selectedVehicle?.activeMissionId?.value?.data ===
        nextProps.selectedVehicle?.activeMissionId?.value?.data &&
    prevProps.selectedVehicle?.vin === nextProps.selectedVehicle?.vin;

export default memo(Map, arePropsEqual);
